<template>
  <div class="media-section ws-grid">
    <div class="media-section__content">
      <section-title :title="$t('section.weAreInMedia.title')" class="media-section__title" />

      <div class="media-section__logos">
        <div
          v-for="logo in $options.logos"
          :key="logo.id"
          class="media-section__logo"
          @mouseover="changeActiveLogo(logo)"
        >
          <img
            :src="logo.imgSrc"
            class="media-section__logo-img"
            :class="getHighlightActiveLogoClass(logo)"
            :alt="logo.alt"
            loading="lazy"
          />
          <div :class="getLogoLineClass(logo)"></div>
        </div>
      </div>
      <div
        class="media-section__quotation u-typography-helvetica u-text u-text--s u-text--m-gte960"
      >
        <div class="media-section__quotation-quotes">&#8220;</div>
        <div class="media-section__quotation-text">
          {{ $t(activeQuotation) }}
        </div>
        <div class="media-section__quotation-quotes">&#8221;</div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from '@/components/common/SectionTitle.vue';

import vogueLogo from '@/assets/images/media-logos/vogue.png';
import tatlerLogo from '@/assets/images/media-logos/tatler.png';
import elleLogo from '@/assets/images/media-logos/elle.png';
import kitLogo from '@/assets/images/media-logos/kit.png';
import femmesLogo from '@/assets/images/media-logos/femmes.png';

export const LOGOS_CONFIGURATION = [
  {
    id: 1,
    imgSrc: tatlerLogo,
    alt: 'tatler',
    quotation: 'weInMedia.tatler'
  },
  {
    id: 2,
    imgSrc: vogueLogo,
    alt: 'vogue',
    quotation: 'weInMedia.vogue'
  },
  {
    id: 3,
    imgSrc: elleLogo,
    alt: 'elle',
    quotation: 'weInMedia.elle'
  },
  {
    id: 4,
    imgSrc: kitLogo,
    alt: 'kit',
    quotation: 'weInMedia.kit'
  },
  {
    id: 5,
    imgSrc: femmesLogo,
    alt: 'femmes',
    quotation: 'weInMedia.femmes'
  }
];

export default {
  name: 'WeInMediaSection',
  logos: LOGOS_CONFIGURATION,
  components: { SectionTitle },
  data() {
    return {
      activeLogoId: 2
    };
  },
  computed: {
    activeQuotation() {
      const logo = LOGOS_CONFIGURATION.find(item => item.id === this.activeLogoId);
      return logo ? logo.quotation : LOGOS_CONFIGURATION[1].quotation;
    }
  },
  methods: {
    getHighlightActiveLogoClass({ id }) {
      return { 'media-section__logo-img-active': this.activeLogoId === id };
    },
    getLogoLineClass({ id }) {
      return { 'media-section__logo-line': this.activeLogoId === id };
    },
    changeActiveLogo({ id }) {
      if (this.activeLogoId === id) {
        return;
      }

      this.activeLogoId = id;
      this.getHighlightActiveLogoClass(id);
      this.getLogoLineClass(id);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~universkin-shared/src/assets/styles/scss/sizes.scss';

.media-section {
  padding: 64px var(--ws-grid-m, 16px);

  &__content {
    max-width: 295px;
    margin: 0 auto;
  }

  &__title {
    text-align: center;
    color: var(--u-color-grey-900);
  }

  &__logos {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin: 0 auto;
    padding-top: 64px;
    gap: 24px;
  }

  &__logo {
    height: 60px;
  }

  &__logo-img {
    height: 20px;
    filter: invert(0.6);
  }

  &__logo-img-active {
    filter: invert(0.1);
  }

  &__logo-line {
    width: 20px;
    border-bottom: 1.6px solid var(--u-color-grey-900);
    padding-top: 15px;
    margin: 0 auto;
  }

  &__quotation {
    width: 100%;
    display: flex;
    justify-content: space-between;
    text-align: center;
    color: var(--u-color-grey-900);
    margin: 64px auto 0;
  }

  &__quotation-quotes {
    color: var(--u-color-grey-500);
    font-size: 3.6rem;
    line-height: 4.3rem;
  }

  &__quotation-text {
    margin: 0 8px;
  }
}

@media (min-width: 600px) {
  .media-section {
    padding: 80px var(--ws-grid-m, 16px);

    &__content {
      max-width: 100%;
    }

    &__logo {
      padding: 0;
    }

    &__logo-img {
      height: 24px;
    }

    &__logo-line {
      width: 31px;
      padding-top: 24px;
    }

    &__quotation {
      width: 415px;
    }

    &__quotation-text {
      margin: 0 24px;
    }
  }
}

@media (min-width: 960px) {
  .media-section {
    padding: 120px var(--ws-grid-m, 16px);

    &__content {
      width: var(--ws-col-10);
    }

    &__logo {
      margin: 0;
    }

    &__logo-img {
      height: 28px;
    }

    &__quotation {
      width: 539px;
    }
  }
}

@media (min-width: 1200px) {
  .media-section {
    &__logo-img {
      height: 38px;
    }
  }
}

@media (min-width: 1440px) {
  .media-section {
    &__quotation {
      width: 667px;
    }

    &__quotation-text {
      margin: 0 40px;
    }
  }
}

@media (min-width: 1920px) {
  .media-section {
    padding: 160px var(--ws-grid-m, 16px);
  }
}
</style>
